import ElectricBoltIcon from "@mui/icons-material/ElectricBolt"
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered"
import HouseIcon from "@mui/icons-material/House"
import InfoIcon from "@mui/icons-material/Info"
import RouteIcon from "@mui/icons-material/Route"
import SendIcon from "@mui/icons-material/Send"
import SummarizeIcon from "@mui/icons-material/Summarize"
import { Stack, Typography } from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { ApplicationCommentsCard } from "../../components/comments/applicationComments/applicationCommentCard"
import {
    AppAccordion,
    AppAccordionDetails,
    AppAccordionSummary,
} from "../../components/dataDisplay/accordions/appAccordions"
import AppChip from "../../components/dataDisplay/chips/appChip"
import StackedKeyValueList from "../../components/dataDisplay/stackedKeyValueList"
import ErrorDisplay from "../../components/fallback/errorDisplay"
import { ConnectionsIcon } from "../../components/icons/icons"
import ApplicationTimeline from "../../components/installerInterface/applicationTimeline"
import InstallerSingleApplicationStatus from "../../components/installerInterface/installerSingleApplicationStatus"
import KpiCircleIcon from "../../components/kpiCards/kpiCircleIcon"
import SingleApplicationSkeleton from "../../components/loading/skeletons/SingleApplicationSkeleton"
import LctChip from "../../components/pills/lct/lctChip"
import AmendmentFailedAlert from "../../components/singleApplication/alerts/amendFailedAlert"
import Attachments from "../../components/singleApplication/attachments/dnoAttachments"
import DetailListAccordion from "../../components/singleApplication/detailAccordions/detailListAccordion"
import NestedDetailListAccordion from "../../components/singleApplication/detailAccordions/nestedDetailListAccordion"
import ExistingDevices from "../../components/singleApplication/existingDevices/existingDevices"
import InstallerApplicationProgressStepper from "../../components/singleApplication/progressStepper/installerApplicationProgressStepper"
import ProposedDevices from "../../components/singleApplication/proposedDevices/proposedDevices"
import { formatKeys } from "../../components/singleApplication/singleApplicationKeys"
import SupplyDetails from "../../components/singleApplication/supplyDetails/supplyDetails"
import {
    AppCard,
    AppCardContent,
    AppCardHeader,
} from "../../components/surfaces/appCard"
import AppPaper from "../../components/surfaces/appPaper"
import DisplayIf from "../../components/wrappers/displayIf"
import {
    APPLICATION_AMENDABLE_STATUSES,
    ApplicationStatuses,
    InstallerApplicationStatuses,
} from "../../constants/enums"
import { NA } from "../../constants/generalConstants"
import {
    TT_AMEND_APPLICATION,
    TT_CX_LAUNCH,
    TT_EXISTING_DEVICES,
    TT_TAD,
    TT_TOTAL_INSTALLED_GEN,
    TT_TOT_DEVICES,
} from "../../constants/tooltips"
import { useScreenSize } from "../../hooks/useScreenSize"
import {
    AMEND_SINGLE_APPLICATION_BASE,
    COMMISSIONING_FORM_BASE,
} from "../../router/routes"
import useInstallerSingleApplication from "../../services/middleware/hooks/installerInterface/useInstallerSingleApplication"
import {
    ExistingDevice,
    TDevicesToInstall,
} from "../../services/middleware/mappers/dnoInterface/dnoApplicationsMapper"
import {
    TCommissioningDeviceData,
    TDisplayActionHistory,
} from "../../services/middleware/types/installerInterface/installerApplicationTypes"
import { GRID_SPACING } from "../../themes/defaultThemeParams"
import {
    arrayOnCondition,
    formatDateTime,
    removeUndefinedAndNulls,
} from "../../utils/general/generalUtils"
import { applicationHasGenerationDevice } from "../../utils/submitApplication/submitApplicationUtils"
import { COLUMNS_DNO_SINGLE } from "../dnoPortal/dnoSingleApplication"
import HeaderBanner from "../lowCarbonTechDatabases/headerBanner"

interface IInstallerSingleApplicationProps {
    applicationId: string
}

/**
 * Single application summary page for Installers. Including screen size
 * responsive layout.
 */
const InstallerSingleApplication = (): ReactElement => {
    const { isLg, isTabletOrMob } = useScreenSize()
    const navigate = useNavigate()
    const { applicationId } = useParams<
        keyof IInstallerSingleApplicationProps
    >() as IInstallerSingleApplicationProps
    const { application, isLoading, isValidating, error, mutate } =
        useInstallerSingleApplication(applicationId)

    if (isLoading) {
        return <SingleApplicationSkeleton />
    }

    if (!application || error) {
        return <ErrorDisplay error={error!} />
    }

    const filteredCustomerDetails = Object.fromEntries(
        Object.entries(application.installerCustomerDetails).filter(
            ([, value]) => value !== null
        )
    ) as Record<string, string | number>

    const filteredCxSiteData = Object.fromEntries(
        Object.entries(application.commissioningInfo?.siteData ?? {}).filter(
            ([, value]) => value !== null
        )
    ) as Record<string, string | number>

    const displayingHistory =
        application.applicationStatus ===
            InstallerApplicationStatuses.cxAccepted ||
        application.applicationStatus ===
            InstallerApplicationStatuses.cxUpdateRequired ||
        application.applicationStatus ===
            InstallerApplicationStatuses.cxSubmitted ||
        application.applicationStatus ===
            InstallerApplicationStatuses.dnoApproved

    const isDnoApprovedWithLimit =
        application.applicationStatus ===
        ApplicationStatuses.dnoApprovedWithLimit

    const showOnlyLatest = [
        ApplicationStatuses.dnoApproved,
        ApplicationStatuses.dnoFurtherInfoRequired,
        ApplicationStatuses.cxSubmitted,
        ApplicationStatuses.autoApproved,
        ApplicationStatuses.dnoApprovedWithLimit,
        ApplicationStatuses.escalatedToDno,
    ].includes(application.applicationStatus as ApplicationStatuses)

    const steps = application.actionHistoryToDisplay
        ? application.actionHistoryToDisplay
              .filter((step) => {
                  if (!step.displayToInstaller) {
                      return false
                  }
                  if (showOnlyLatest) {
                      return step.isLatest
                  }
                  return true
              })
              .map((step: TDisplayActionHistory) => ({
                  labelBold: step.title,
                  labelFeint: formatDateTime(step.time),
                  description: (
                      <StackedKeyValueList
                          details={removeUndefinedAndNulls(step.details)}
                      />
                  ),
              }))
        : []
    return (
        <Grid
            container
            display="flex"
            alignContent="flex-start"
            spacing={GRID_SPACING}
            columns={COLUMNS_DNO_SINGLE}
        >
            <Grid xs={COLUMNS_DNO_SINGLE}>
                <HeaderBanner
                    title="Application Summary"
                    subtitle={
                        (application.installerCustomerDetails
                            .mainAddress as string) || "Address Not available"
                    }
                    subSubtitle={`${application.applicationId} \u2022 ${application.applicationStatus}`}
                    redacted={application.isRedacted}
                    reSubmitted={application.isResubmit}
                    actions={[
                        ...arrayOnCondition(application.isReadyForCx, [
                            {
                                text: "Commissioning",
                                color: "secondary" as const,
                                endIcon: <SendIcon />,
                                tooltip: TT_CX_LAUNCH,
                                onClick: () =>
                                    navigate(
                                        `${COMMISSIONING_FORM_BASE}/${applicationId}`
                                    ),
                            },
                        ]),
                    ]}
                    IsAmendable={
                        APPLICATION_AMENDABLE_STATUSES.includes(
                            application.applicationStatus as InstallerApplicationStatuses
                        )
                            ? {
                                  text: "Amend",
                                  color: "primary" as const,
                                  endIcon: <SendIcon />,
                                  tooltip: TT_AMEND_APPLICATION,
                                  onClick: () =>
                                      navigate(
                                          `${AMEND_SINGLE_APPLICATION_BASE}/${applicationId}`
                                      ),
                              }
                            : undefined
                    }
                />
            </Grid>
            <Grid xl={3} lg={4} xs={COLUMNS_DNO_SINGLE}>
                <Grid
                    container
                    spacing={GRID_SPACING}
                    columns={COLUMNS_DNO_SINGLE}
                >
                    <Grid
                        xs={COLUMNS_DNO_SINGLE}
                        sm={7}
                        lg={COLUMNS_DNO_SINGLE}
                    >
                        <AppCard sx={{ height: "100%" }}>
                            <AppCardHeader
                                avatar={<SummarizeIcon fontSize="large" />}
                                title="Summary"
                                subheader="Quick Facts"
                                titleTypographyProps={{
                                    variant: "h5",
                                    fontWeight: "500",
                                }}
                            />
                            <AppCardContent sx={{ overflow: "none" }}>
                                <Stack spacing={1}>
                                    <Stack
                                        spacing={1}
                                        direction="row"
                                        useFlexGap
                                        flexWrap="wrap"
                                    >
                                        <AppChip
                                            sx={{ mr: 1 }}
                                            label={
                                                application.applicationStatus
                                            }
                                            variant="outlined"
                                            color="primary"
                                        />
                                        {application.lcts.map((lct) => (
                                            <LctChip
                                                key={lct}
                                                lct={lct}
                                                sx={{ mr: 1 }}
                                            />
                                        ))}
                                    </Stack>
                                    <StackedKeyValueList
                                        details={application.summaryFacts}
                                        spacing={1}
                                    />
                                </Stack>
                            </AppCardContent>
                        </AppCard>
                    </Grid>
                    <Grid
                        xs={COLUMNS_DNO_SINGLE}
                        sm={8}
                        lg={COLUMNS_DNO_SINGLE}
                    >
                        <Stack spacing={GRID_SPACING}>
                            <KpiCircleIcon
                                title="Number of Devices"
                                value={String(
                                    application.devicesToInstall.length
                                )}
                                icon={FormatListNumberedIcon}
                                tooltip={TT_TOT_DEVICES}
                            />
                            {application?.newPremisesGenerationCapacityKw
                                .newPremisesGenerationCapacityKw &&
                            application?.newPremisesGenerationCapacityKw
                                .newPremisesGenerationCapacityKw !== NA ? (
                                <KpiCircleIcon
                                    title="Total Installed Generation"
                                    value={`${application.newPremisesGenerationCapacityKw.newPremisesGenerationCapacityKw} kW`}
                                    color="secondary"
                                    icon={ConnectionsIcon}
                                    tooltip={TT_TOTAL_INSTALLED_GEN}
                                />
                            ) : (
                                <KpiCircleIcon
                                    title="Existing Devices on Site"
                                    value={String(
                                        application.existingDevices.length
                                    )}
                                    icon={HouseIcon}
                                    color="secondary"
                                    tooltip={TT_EXISTING_DEVICES}
                                />
                            )}
                            <KpiCircleIcon
                                title="Total Demand"
                                value={`${application.newPremisesMaxDemand.newPremisesMaxDemand} Amps`}
                                icon={ConnectionsIcon}
                                tooltip={TT_TAD}
                            />
                        </Stack>
                    </Grid>
                    {isLg && (
                        <Grid xs={COLUMNS_DNO_SINGLE} xl={COLUMNS_DNO_SINGLE}>
                            <Attachments
                                applicationId={application.applicationId}
                                attachments={application.cutOutImages}
                                additionalAttachments={
                                    application.additionalAttachments
                                }
                                isDnoMode={false}
                            />
                        </Grid>
                    )}
                </Grid>
            </Grid>
            <Grid
                container
                display="flex"
                alignContent="flex-start"
                xl={12}
                lg={11}
                xs={COLUMNS_DNO_SINGLE}
            >
                {!isTabletOrMob && (
                    <Grid xl={COLUMNS_DNO_SINGLE} xs={COLUMNS_DNO_SINGLE}>
                        <AppCard>
                            <AppCardHeader
                                avatar={<RouteIcon fontSize="large" />}
                                title="Application Progress"
                                subheader="Journey of the application through Connect Direct"
                                titleTypographyProps={{
                                    variant: "h5",
                                    fontWeight: "500",
                                }}
                            />
                            <AppCardContent>
                                <InstallerApplicationProgressStepper
                                    status={application.applicationStatus}
                                    dno={application.dno}
                                    isGeneration={applicationHasGenerationDevice(
                                        application
                                    )}
                                />
                            </AppCardContent>
                        </AppCard>
                    </Grid>
                )}

                <Grid
                    container
                    display="flex"
                    alignContent="flex-start"
                    xl={7.5}
                    xs={COLUMNS_DNO_SINGLE}
                >
                    <Grid lg={COLUMNS_DNO_SINGLE} xs={COLUMNS_DNO_SINGLE}>
                        <AppPaper>
                            <Stack spacing={GRID_SPACING}>
                                <AmendmentFailedAlert
                                    lastAmendmentFailed={
                                        !!application.lastAmendmentFailed
                                    }
                                    amendmentFailureCategory={
                                        application.amendmentFailureCategory ||
                                        ""
                                    }
                                />
                                <InstallerSingleApplicationStatus
                                    status={application.applicationStatus}
                                    dnoComments={
                                        application.dnoApplicationResponse
                                            ?.comment as string
                                    }
                                    condition={
                                        !!application.dnoApplicationResponse
                                            ?.comment &&
                                        !displayingHistory &&
                                        isDnoApprovedWithLimit
                                    }
                                    dno={application.dno}
                                    cxRef={
                                        application.isReadyForCx &&
                                        application.applicationStatus !==
                                            InstallerApplicationStatuses.cxUpdateRequired
                                    }
                                >
                                    <DisplayIf
                                        condition={isDnoApprovedWithLimit}
                                    >
                                        <>
                                            <DisplayIf
                                                condition={
                                                    !!application
                                                        .dnoApplicationResponse
                                                        ?.demandLimit
                                                }
                                            >
                                                <>
                                                    <Typography>
                                                        {`${application.dno} has requested you `}
                                                        <strong>
                                                            limit the import
                                                            demand
                                                        </strong>
                                                        {` of this property to `}
                                                        <strong>
                                                            {
                                                                application
                                                                    .dnoApplicationResponse
                                                                    ?.demandLimit
                                                            }
                                                        </strong>
                                                        .
                                                        <br />
                                                        This has been done for
                                                        the following reasons:
                                                    </Typography>
                                                    <ul>
                                                        {application.dnoApplicationResponse?.demandLimitReasons?.map(
                                                            (limitReason) => (
                                                                <li
                                                                    key={
                                                                        limitReason
                                                                    }
                                                                >
                                                                    <Typography>
                                                                        {
                                                                            limitReason
                                                                        }
                                                                    </Typography>
                                                                </li>
                                                            )
                                                        )}
                                                    </ul>
                                                </>
                                            </DisplayIf>
                                            <DisplayIf
                                                condition={
                                                    !!application
                                                        .dnoApplicationResponse
                                                        ?.generationLimit
                                                }
                                            >
                                                <>
                                                    <Typography>
                                                        {`${application.dno} requested you `}
                                                        <strong>
                                                            limit the export
                                                        </strong>
                                                        {` of this property to `}
                                                        <strong>
                                                            {
                                                                application
                                                                    .dnoApplicationResponse
                                                                    ?.generationLimit
                                                            }
                                                        </strong>
                                                        . This has been done for
                                                        the following reasons:
                                                    </Typography>
                                                    <ul>
                                                        {application.dnoApplicationResponse?.generationLimitReasons?.map(
                                                            (
                                                                generationReason
                                                            ) => (
                                                                <li
                                                                    key={
                                                                        generationReason
                                                                    }
                                                                >
                                                                    <Typography>
                                                                        {
                                                                            generationReason
                                                                        }
                                                                    </Typography>
                                                                </li>
                                                            )
                                                        )}
                                                    </ul>
                                                </>
                                            </DisplayIf>
                                        </>
                                    </DisplayIf>
                                </InstallerSingleApplicationStatus>
                                <DisplayIf condition={steps.length > 0}>
                                    <>
                                        <Typography fontWeight={500}>
                                            Action History
                                        </Typography>
                                        <ApplicationTimeline
                                            applicationSteps={steps}
                                        />
                                    </>
                                </DisplayIf>
                            </Stack>
                        </AppPaper>
                    </Grid>
                    <Grid lg={COLUMNS_DNO_SINGLE} xs={COLUMNS_DNO_SINGLE}>
                        <AppCard>
                            <AppCardHeader
                                avatar={<ElectricBoltIcon fontSize="large" />}
                                title={`Proposed Device${
                                    application.existingDevices.length > 1
                                        ? "s"
                                        : ""
                                }`}
                                subheader="Outlines the equipment the customer wishes to install"
                                titleTypographyProps={{
                                    variant: "h5",
                                    fontWeight: "500",
                                }}
                            />
                            <AppCardContent>
                                <ProposedDevices
                                    proposedDevices={
                                        application.devicesToInstall as TDevicesToInstall[]
                                    }
                                />
                            </AppCardContent>
                        </AppCard>
                    </Grid>
                </Grid>
                <Grid
                    display="flex"
                    alignContent="flex-start"
                    container
                    xl={7.5}
                    xs={COLUMNS_DNO_SINGLE}
                >
                    <Grid
                        lg={COLUMNS_DNO_SINGLE}
                        xs={COLUMNS_DNO_SINGLE}
                        width="100%"
                    >
                        <AppCard>
                            <AppCardHeader
                                avatar={<InfoIcon fontSize="large" />}
                                title="Other Details"
                                subheader="Click into the boxes to see further information"
                                titleTypographyProps={{
                                    variant: "h5",
                                    fontWeight: "500",
                                }}
                            />
                            <AppCardContent>
                                <Stack spacing={GRID_SPACING}>
                                    {application.commissioningInfo && (
                                        <NestedDetailListAccordion
                                            title="Commissioning Details"
                                            kwargs={{
                                                "Site Information":
                                                    filteredCxSiteData
                                                        ? formatKeys(
                                                              filteredCxSiteData
                                                          )
                                                        : {}, // Provide an empty object as fallback
                                                ...(application
                                                    .commissioningInfo.devices
                                                    ? application.commissioningInfo.devices.reduce<
                                                          Record<
                                                              string,
                                                              TCommissioningDeviceData
                                                          >
                                                      >(
                                                          (
                                                              acc,
                                                              deviceInfo
                                                          ) => ({
                                                              ...acc,
                                                              [`Details for device: ${deviceInfo.deviceSysRef}`]:
                                                                  formatKeys(
                                                                      deviceInfo
                                                                  ),
                                                          }),
                                                          {}
                                                      )
                                                    : {}),
                                            }}
                                        />
                                    )}
                                    <DetailListAccordion
                                        title="Customer and Installer Details"
                                        kwargs={formatKeys(
                                            filteredCustomerDetails
                                        )}
                                    />
                                    <AppAccordion>
                                        <AppAccordionSummary>
                                            <Typography fontWeight="500">
                                                Supply Details
                                            </Typography>
                                        </AppAccordionSummary>
                                        <AppAccordionDetails>
                                            <SupplyDetails
                                                applicationId={applicationId}
                                                supplyDetails={
                                                    application.supplyDetails
                                                }
                                                disabled={
                                                    application.isRedacted
                                                }
                                            />
                                        </AppAccordionDetails>
                                    </AppAccordion>
                                    <AppAccordion>
                                        <AppAccordionSummary>
                                            <Typography fontWeight="500">
                                                {`Existing Devices (${application.existingDevices.length})`}
                                            </Typography>
                                        </AppAccordionSummary>
                                        <AppAccordionDetails>
                                            <ExistingDevices
                                                existingDevices={
                                                    application.existingDevices as ExistingDevice[]
                                                }
                                            />
                                        </AppAccordionDetails>
                                    </AppAccordion>
                                </Stack>
                            </AppCardContent>
                        </AppCard>
                    </Grid>
                    <Grid>
                        <ApplicationCommentsCard
                            title="Comments"
                            subheader="Use this thread to communicate with the DNO reviewing your application. Please note, comments posted here cannot be deleted and are supplementary to your application"
                            applicationId={application.applicationId}
                            mutate={mutate}
                            isDno={false}
                            isRedacted={application.isRedacted}
                            fetchingComments={isLoading || isValidating}
                            comments={application.comments ?? []}
                            commentStatus={application.isInstallerThreadUnread}
                            lastReadBy={application.lastReadByInstallerUsername}
                            lastReadAt={
                                application.lastReadByInstallerTimestamp
                            }
                        />
                    </Grid>
                </Grid>
                {!isLg && (
                    <Grid xs={COLUMNS_DNO_SINGLE} xl={COLUMNS_DNO_SINGLE}>
                        <Stack spacing={GRID_SPACING}>
                            <Attachments
                                applicationId={application.applicationId}
                                attachments={application.cutOutImages}
                                additionalAttachments={
                                    application.additionalAttachments
                                }
                                isDnoMode={false}
                                disabled={application.isRedacted}
                            />
                            {application.commissioningInfo &&
                                application.commissioningInfo.files.length !==
                                    0 && (
                                    <Attachments
                                        title="Commissioning Attachments"
                                        applicationId={applicationId}
                                        attachments={
                                            application.commissioningInfo.files
                                        }
                                        additionalAttachments={[]}
                                        isDnoMode
                                        disabled={application.isRedacted}
                                    />
                                )}
                        </Stack>
                    </Grid>
                )}
            </Grid>
        </Grid>
    )
}

export default InstallerSingleApplication
